import { TextField, useRecordContext } from 'react-admin';

import { GetMigratedCampaignOneResponse } from '@Common/types/bo/b2b/campaigns/migrated';

import { ShowFields, ShowSection, ShowWithNavigation } from '@sh4res/bo-common/components';
import {
  b2bMigratedCampaignStatusChoices,
  b2bMigratedCampaignSubtypeChoices,
} from '@sh4res/bo-common/components/B2bCampaigns/choices';
import {
  ChipFieldsList,
  CurrencyField,
  IdField,
  SelectChipField,
  UTCDateField,
} from '@sh4res/bo-common/fields';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { Resources } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

const testId = 'B2bMigratedCampaignsShow';

const B2bMigratedCampaignDetails = () => {
  const resourceBasePath = useResourceBasePath();
  const resource = `${resourceBasePath}campaigns/migrated` as Resources;
  const record = useRecordContext<GetMigratedCampaignOneResponse>();
  const isExternal = isBoExternal();

  if (!record) {
    return null;
  }

  return (
    <ShowSection
      label="resources.bo_common.b2b.campaigns.migrated.sections.details"
      resource={resource}>
      <ShowFields<GetMigratedCampaignOneResponse>
        labels={{
          id: 'resources.bo_common.b2b.campaigns.migrated.fields.id',
          b2b_company_id: 'resources.bo_common.b2b.campaigns.migrated.fields.b2b_company_id',
          subtype: 'resources.bo_common.b2b.campaigns.migrated.fields.subtype',
          status: 'resources.bo_common.b2b.campaigns.migrated.fields.status',
          processing_start_date:
            'resources.bo_common.b2b.campaigns.migrated.fields.processing_start_date',
          processing_end_date:
            'resources.bo_common.b2b.campaigns.migrated.fields.processing_end_date',
          fiscal_period_start:
            'resources.bo_common.b2b.campaigns.migrated.fields.fiscal_period_start',
          fiscal_period_end: 'resources.bo_common.b2b.campaigns.migrated.fields.fiscal_period_end',
          amount_brut: 'resources.bo_common.b2b.campaigns.migrated.fields.amount_brut',
          amount_net: 'resources.bo_common.b2b.campaigns.migrated.fields.amount_net',
          created_at: 'resources.bo_common.b2b.campaigns.migrated.fields.created_at',
        }}
        fields={{
          id: <TextField data-testid={`${testId}_IdField`} />,
          b2b_company_id: (
            <IdField
              reference={`${resourceBasePath}companies`}
              data-testid={`${testId}_CompanyIdField`}
            />
          ),
          subtype: (
            <SelectChipField
              choices={b2bMigratedCampaignSubtypeChoices}
              displayRawValueIfChoiceNotFound
              data-testid={`${testId}_SubtypeField`}
            />
          ),
          status: (
            <SelectChipField
              choices={b2bMigratedCampaignStatusChoices}
              displayRawValueIfChoiceNotFound
              data-testid={`${testId}_StatusField`}
            />
          ),
          processing_start_date: (
            <UTCDateField showTime={false} data-testid={`${testId}_ProcessingStartDateField`} />
          ),
          processing_end_date: (
            <UTCDateField showTime={false} data-testid={`${testId}_ProcessingEndDateField`} />
          ),
          fiscal_period_start: (
            <UTCDateField showTime={false} data-testid={`${testId}_FiscalPeriodStartField`} />
          ),
          fiscal_period_end: (
            <UTCDateField showTime={false} data-testid={`${testId}_FiscalPeriodEndField`} />
          ),
          amount_brut: (
            <CurrencyField variant="micro" currency="EUR" testId={`${testId}_AmountBrutField`} />
          ),
          amount_net: (
            <CurrencyField variant="micro" currency="EUR" testId={`${testId}_AmountNetField`} />
          ),
          created_at: <UTCDateField showTime={false} data-testid={`${testId}_CreatedAtField`} />,
          // Internal-only fields: not shown to external BO (business correspondents).
          ...(isExternal
            ? {}
            : {
                company_name: <TextField data-testid={`${testId}_CompanyNameField`} />,
                type: <TextField data-testid={`${testId}_TypeField`} />,
                source_instrument: <TextField data-testid={`${testId}_SourceInstrumentField`} />,
                target_instrument: <TextField data-testid={`${testId}_TargetInstrumentField`} />,
                source_plan_piloted: <TextField data-testid={`${testId}_SourcePlanPilotedField`} />,
                source_plan_libre: <TextField data-testid={`${testId}_SourcePlanLibreField`} />,
                target_plan_piloted: <TextField data-testid={`${testId}_TargetPlanPilotedField`} />,
                target_plan_libre: <TextField data-testid={`${testId}_TargetPlanLibreField`} />,
                plan_types: <ChipFieldsList source="plan_types" />,
              }),
        }}
      />
    </ShowSection>
  );
};

export const B2bMigratedCampaignsShow = () => (
  <ShowWithNavigation navigateTo={() => -1} actions={false}>
    <B2bMigratedCampaignDetails />
  </ShowWithNavigation>
);
